import { useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';

import useSecuredApiV2 from './use-secured-api-v2';
import {
  type SiteMenuOrderDetailDto,
  toSiteMenuOrder,
} from '../dto/customer-order/customer-order-detail-dto';
import { type ResponseDto } from '../dto/ResponseDto';
import { type SiteMenuOrder } from '../entities';
import {
  ARCHIVED_CUSTOMER_ORDER_END_POINT_URL,
  CUSTOMER_ORDER_END_POINT_URL,
} from '../utils/endpoints';
import type ProblemDetailsDto from '../dto/error/problem-details-dto';

export interface OrderSiteMenuOrdersResult {
  data: SiteMenuOrder[];
  isLoading: boolean;
  errorMessage?: string;
}

/**
 * Loads the line items of an order from the customer order controller, or from the archived customer order
 * controller when "isArchived". For an active order it loads the given revision, or the latest one when
 * "revisionId" is left out
 */
const useOrderSiteMenuOrders = (
  orderId: string | undefined,
  revisionId: string | undefined,
  isArchived: boolean
): OrderSiteMenuOrdersResult => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<OrderSiteMenuOrdersResult>({ data: [], isLoading: false });

  useEffect(() => {
    if (!orderId) {
      setState({ data: [], isLoading: false });
      return;
    }
    setState({ data: [], isLoading: true });

    const url = isArchived
      ? `${ARCHIVED_CUSTOMER_ORDER_END_POINT_URL}/${orderId}/site-menu-orders`
      : `${CUSTOMER_ORDER_END_POINT_URL}/${orderId}/site-menu-orders`;

    const subscription = api
      .get(url, isArchived || !revisionId ? {} : { revisionId }, {})
      .pipe(
        tap((response: ResponseDto<SiteMenuOrderDetailDto[]>) => {
          setState({ data: response.data.map(toSiteMenuOrder), isLoading: false });
        }),
        catchError((error: ProblemDetailsDto) => {
          // Clear the items, so the ones loaded for another order or revision aren't shown here
          setState({
            data: [],
            isLoading: false,
            errorMessage: error.detail ?? 'Failed to load the meals of this order',
          });
          return EMPTY;
        })
      )
      .subscribe();

    // Ignore the response if the order or the revision changes, or the component unmounts, before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, [orderId, revisionId, isArchived]);

  return state;
};

export default useOrderSiteMenuOrders;
